import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  getEvents, getEventAttendance, initEventAttendance, updateAttendance, 
  getUsers, getGroups, getEventAttendanceStats 
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  UserCheck, Calendar, CheckCircle2, XCircle, Clock, 
  AlertCircle, Users, Filter, RefreshCw 
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const STATUS_OPTIONS = [
  { value: 'pendiente', label: 'Pendiente', icon: Clock, color: 'bg-stone-100 text-stone-700' },
  { value: 'presente', label: 'Presente', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'ausente', label: 'Ausente', icon: XCircle, color: 'bg-red-100 text-red-700' },
  { value: 'justificado', label: 'Justificado', icon: AlertCircle, color: 'bg-amber-100 text-amber-700' }
];

export default function Attendance() {
  const { hasPermission } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchEventAttendance(selectedEvent.id);
    }
  }, [selectedEvent]);

  const fetchData = async () => {
    try {
      const [eventsRes, usersRes, groupsRes] = await Promise.all([
        getEvents(),
        getUsers(),
        getGroups()
      ]);
      // Filter events that require attendance
      const attendanceEvents = eventsRes.data.filter(e => e.requires_attendance !== false);
      setEvents(attendanceEvents);
      setUsers(usersRes.data);
      setGroups(groupsRes.data);
      
      if (attendanceEvents.length > 0) {
        setSelectedEvent(attendanceEvents[0]);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventAttendance = async (eventId) => {
    try {
      const [attendanceRes, statsRes] = await Promise.all([
        getEventAttendance(eventId),
        getEventAttendanceStats(eventId)
      ]);
      setAttendance(attendanceRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const handleInitAttendance = async () => {
    if (!selectedEvent) return;
    try {
      await initEventAttendance(selectedEvent.id);
      toast.success('Asistencia inicializada');
      fetchEventAttendance(selectedEvent.id);
    } catch (error) {
      toast.error('Error al inicializar asistencia');
    }
  };

  const handleUpdateStatus = async (recordId, newStatus) => {
    try {
      await updateAttendance(recordId, newStatus);
      toast.success('Asistencia actualizada');
      fetchEventAttendance(selectedEvent.id);
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getUserById = (userId) => {
    return users.find(u => u.id === userId) || {};
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "EEEE d 'de' MMMM, HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return opt;
  };

  const filteredAttendance = filterStatus === 'all' 
    ? attendance 
    : attendance.filter(a => a.status === filterStatus);

  const canManage = hasPermission('eventos', 'editar');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="attendance-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-emerald-900 font-['Outfit']">
            Control de Asistencia
          </h1>
          <p className="text-muted-foreground">
            Registro de participación en eventos y ensayos
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Events List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Eventos
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-y-auto">
              {events.map((event) => (
                <button
                  key={event.id}
                  onClick={() => setSelectedEvent(event)}
                  className={`w-full p-4 text-left border-b hover:bg-stone-50 transition-colors ${
                    selectedEvent?.id === event.id ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : ''
                  }`}
                  data-testid={`event-${event.id}`}
                >
                  <p className="font-medium">{event.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(event.start_date)}
                  </p>
                  <Badge variant="outline" className="mt-1">
                    {event.event_type}
                  </Badge>
                </button>
              ))}
              {events.length === 0 && (
                <p className="p-4 text-center text-muted-foreground">
                  No hay eventos programados
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Attendance Details */}
        <Card className="lg:col-span-2">
          {selectedEvent ? (
            <>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{selectedEvent.title}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatDate(selectedEvent.start_date)}
                      {selectedEvent.location && ` • ${selectedEvent.location}`}
                    </p>
                  </div>
                  {canManage && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleInitAttendance}
                        data-testid="init-attendance-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Inicializar
                      </Button>
                    </div>
                  )}
                </div>

                {/* Stats */}
                {stats && stats.total > 0 && (
                  <div className="mt-4 grid grid-cols-4 gap-2">
                    {STATUS_OPTIONS.map((opt) => {
                      const count = stats[opt.value === 'pendiente' ? 'pending' : opt.value === 'presente' ? 'present' : opt.value === 'ausente' ? 'absent' : 'justified'];
                      return (
                        <div key={opt.value} className={`p-2 rounded-lg text-center ${opt.color}`}>
                          <p className="text-lg font-bold">{count || 0}</p>
                          <p className="text-xs">{opt.label}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {stats && stats.total > 0 && (
                  <div className="mt-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Tasa de asistencia</span>
                      <span className="font-medium">{stats.attendance_rate.toFixed(1)}%</span>
                    </div>
                    <Progress value={stats.attendance_rate} className="h-2" />
                  </div>
                )}

                {/* Filter */}
                <div className="mt-4 flex items-center gap-2">
                  <Filter className="w-4 h-4 text-muted-foreground" />
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40" data-testid="attendance-filter">
                      <SelectValue placeholder="Filtrar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>

              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Miembro</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Hora</TableHead>
                        {canManage && <TableHead className="text-right">Acciones</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAttendance.map((record) => {
                        const user = getUserById(record.user_id);
                        const statusInfo = getStatusBadge(record.status);
                        const Icon = statusInfo.icon;
                        return (
                          <TableRow key={record.id}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="w-8 h-8">
                                  <AvatarImage src={user.avatar_url} />
                                  <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">
                                    {getInitials(user.full_name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-medium">{user.full_name || 'Usuario'}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={statusInfo.color}>
                                <Icon className="w-3 h-3 mr-1" />
                                {statusInfo.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {record.check_in_time ? format(parseISO(record.check_in_time), 'HH:mm') : '-'}
                            </TableCell>
                            {canManage && (
                              <TableCell className="text-right">
                                <Select
                                  value={record.status}
                                  onValueChange={(value) => handleUpdateStatus(record.id, value)}
                                >
                                  <SelectTrigger className="w-32" data-testid={`status-${record.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {STATUS_OPTIONS.map((opt) => (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      {filteredAttendance.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                            {attendance.length === 0 
                              ? 'Haz clic en "Inicializar" para crear los registros de asistencia'
                              : 'No hay registros con el filtro seleccionado'
                            }
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </>
          ) : (
            <CardContent className="py-12 text-center text-muted-foreground">
              <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Selecciona un evento para ver la asistencia</p>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
